import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Typography, theme, Avatar, Space, Dropdown } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BoxPlotOutlined,
  LogoutOutlined,
  ProfileOutlined,
  SettingOutlined,
  UserOutlined
} from '@ant-design/icons';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationCenter from '../realtime/NotificationCenter';
import { useAuthStore } from '../../stores/authStore';
import { useTranslation } from "react-i18next";

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const PickUserLayout = () => {
    const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 处理登出
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: t('個人資料'),
      onClick: () => navigate('/settings/profile')
    },
    // CUSTOMER 角色不顯示設定選項
    ...(user?.role !== 'CUSTOMER' ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('設定'),
      onClick: () => navigate('/settings')
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('登出'),
      onClick: handleLogout
    }
  ];

  const menuItems = [
    {
      key: 'inventory/picking',
      icon: <BoxPlotOutlined />,
      label: t('Picking Management'),
    }
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(`/${key}`);
  };

  const getSelectedKeys = () => {
    const path = location.pathname.slice(1); // Remove leading slash
    
    // Check if it's a nested route
    for (const item of menuItems) {
      if (path === item.key || path.startsWith(item.key + '/')) {
        return [item.key];
      }
    }
    
    // 默认选中拣货管理
    return ['inventory/picking'];
  };

  const getPageTitle = () => {
    const path = location.pathname.slice(1);
    
    // Check nested routes first
    for (const item of menuItems) {
      if (path === item.key) {
        return item.label;
      }
    }
    
    return t('Picking Management');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} width={250}>
        <div style={{ 
          height: '64px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'white',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          {collapsed ? 'WYNI' : 'WYNI ERP'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      
      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: colorBgContainer, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <Space style={{ flex: 1, justifyContent: 'center' }}>
            <Title level={4} style={{ 
              margin: 0, 
              color: '#011f4b',
              fontSize: '20px',
            }}>
              {getPageTitle()}
            </Title>
          </Space>
          <Space>
            <LanguageSwitcher />
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer', padding: '8px' }}>
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#005b96' }}
                />
                <span style={{ color: '#011f4b' }}>
                  {user?.name || user?.email || t('用戶')}
                </span>
              </Space>
            </Dropdown>
            <NotificationCenter companyId="demo_company" />
          </Space>
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default PickUserLayout;